import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  authedFetch,
  hasStoredSession,
  type StoredAuth,
  useStoredAuth,
} from "nazare-ui/utils";
import { resolveBffDomain } from "./bff-domain";

export type DataSource = "postgres" | "dynamodb" | "s3";

export type EntityGraphNode = {
  id: string;
  label: string;
  source: DataSource;
  kind: string;
  name?: string;
  schema?: string;
  namespace?: string;
  fields?: string[];
  metadata?: Record<string, string | number | boolean | null | undefined>;
};

export type EntityGraphEdge = {
  id?: string;
  source: string;
  target: string;
  label?: string;
  kind?: string;
  confidence?: "declared" | "inferred";
  metadata?: {
    sourceColumns?: string[];
    targetColumns?: string[];
    sourceField?: string;
    targetField?: string;
    key?: string;
    validated?: boolean;
  };
};

export type EntityGraph = {
  nodes: EntityGraphNode[];
  edges: EntityGraphEdge[];
};

export type CatalogueResource = {
  id: string;
  name: string;
  namespace?: string;
  kind: string;
  metadata: {
    estimatedRows?: number;
    sizeBytes?: number;
    createdAt?: string;
    selectable?: boolean;
    identityArguments?: string;
    resultType?: string;
  };
};

export type DataColumn = {
  name: string;
  dataType?: string;
  nullable?: boolean;
  primaryKey?: boolean;
};

export type RecordFilter = {
  field?: string | null;
  op?: "contains" | "equals" | "starts" | "gt" | "lt" | "is-null" | null;
  value?: string | null;
};

export type DataPageRequest = RecordFilter & {
  source: DataSource;
  resource: CatalogueResource;
  q: string;
  prefix?: string;
  sort?: string | null;
  direction?: "asc" | "desc";
  limit: number;
  cursor?: string | null;
};

export type PostgresPage = {
  source: "postgres";
  columns: Array<{
    name: string;
    data_type: string;
    nullable: boolean;
    position: number;
    primary_key: boolean;
  }>;
  rows: Record<string, unknown>[];
  nextCursor: string | null;
};

export type DynamoPage = {
  source: "dynamodb";
  columns: string[];
  rows: Record<string, unknown>[];
  nextCursor: string | null;
  metadata: {
    itemCount: number | null;
    sizeBytes: number | null;
    status: string | null;
    keySchema: Array<{ AttributeName?: string; KeyType?: string }>;
  };
  scanned: number;
};

export type S3Entry = {
  key: string;
  kind: "prefix" | "object";
  size?: number;
  lastModified?: string | null;
  storageClass?: string | null;
};

export type S3Page = {
  source: "s3";
  entries: S3Entry[];
  nextCursor: string | null;
  inspected: number;
};

export type DataPage = PostgresPage | DynamoPage | S3Page;

export type S3ObjectPreview = {
  key: string;
  contentType: string;
  size: number;
  lastModified: string | null;
  etag: string | null;
  url: string;
  text: string | null;
  truncated: boolean;
};

const DATA_EXPLORER_BASE = `${resolveBffDomain()}/data-explorer`;

async function readJson<T>(response: Response, label: string): Promise<T> {
  if (response.ok) return response.json() as Promise<T>;
  const body = (await response.json().catch(() => null)) as {
    error?: string | { message?: string };
  } | null;
  const detail =
    typeof body?.error === "string" ? body.error : body?.error?.message;
  throw new Error(detail ?? `${label}: ${response.status}`);
}

async function fetchCatalogue(
  auth: StoredAuth | null,
  source: DataSource,
): Promise<CatalogueResource[]> {
  const params = new URLSearchParams({ source });
  const response = await authedFetch(
    DATA_EXPLORER_BASE,
    auth,
    `/catalogue?${params}`,
  );
  const data = await readJson<{ resources: CatalogueResource[] }>(
    response,
    "Failed to load data catalogue",
  );
  return data.resources;
}

export function useDataCatalogue(source: DataSource, enabled = true) {
  const auth = useStoredAuth();
  return useQuery({
    queryKey: ["data-explorer", "catalogue", source],
    queryFn: () => fetchCatalogue(auth, source),
    enabled: enabled && hasStoredSession(auth),
    staleTime: 60_000,
  });
}

export function useEntityGraph(enabled = true) {
  const auth = useStoredAuth();
  return useQuery({
    queryKey: ["data-explorer", "entity-graph", "relations-v1"],
    queryFn: async () => {
      const response = await authedFetch(DATA_EXPLORER_BASE, auth, "/graph");
      return readJson<EntityGraph>(response, "Failed to load entity map");
    },
    enabled: enabled && hasStoredSession(auth),
    staleTime: 5 * 60_000,
  });
}

function dataPagePath(request: DataPageRequest): string {
  if (request.source === "postgres") {
    return `/postgres/${encodeURIComponent(request.resource.namespace ?? "")}/${encodeURIComponent(request.resource.name)}`;
  }
  return `/${request.source}/${encodeURIComponent(request.resource.name)}`;
}

function dataPageParams(request: DataPageRequest): URLSearchParams {
  const params = new URLSearchParams({
    limit: String(request.limit),
  });
  if (request.q.trim()) params.set("q", request.q.trim());
  if (request.cursor) params.set("cursor", request.cursor);
  if (request.source === "s3") {
    if (request.prefix) params.set("prefix", request.prefix);
    return params;
  }
  if (request.field) params.set("field", request.field);
  if (request.op) params.set("op", request.op);
  if (request.value) params.set("value", request.value);
  if (request.sort) params.set("sort", request.sort);
  if (request.direction) params.set("direction", request.direction);
  return params;
}

async function fetchDataPage(
  auth: StoredAuth | null,
  request: DataPageRequest,
): Promise<DataPage> {
  const response = await authedFetch(
    DATA_EXPLORER_BASE,
    auth,
    `${dataPagePath(request)}?${dataPageParams(request)}`,
  );
  return readJson<DataPage>(response, "Failed to read data");
}

export function useInfiniteData(request: DataPageRequest | null) {
  const auth = useStoredAuth();
  return useInfiniteQuery({
    queryKey: ["data-explorer", "pages", request],
    queryFn: ({ pageParam }) =>
      fetchDataPage(auth, { ...request!, cursor: pageParam }),
    enabled: hasStoredSession(auth) && request !== null,
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 15_000,
  });
}

async function fetchS3Object(
  auth: StoredAuth | null,
  bucket: string,
  key: string,
): Promise<S3ObjectPreview> {
  const params = new URLSearchParams({ key });
  const response = await authedFetch(
    DATA_EXPLORER_BASE,
    auth,
    `/s3/${encodeURIComponent(bucket)}/object?${params}`,
  );
  return readJson<S3ObjectPreview>(response, "Failed to load object preview");
}

export function useS3Object(bucket: string | null, key: string | null) {
  const auth = useStoredAuth();
  return useQuery({
    queryKey: ["data-explorer", "s3-object", bucket, key],
    queryFn: () => fetchS3Object(auth, bucket!, key!),
    enabled: hasStoredSession(auth) && !!bucket && !!key,
    staleTime: 5 * 60_000,
  });
}
